import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Globe,
  Radio,
  MessageSquare,
  DollarSign,
  Users,
  Settings,
  FlaskConical,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { ReactComponent as SCELogo } from '../assets/SCELogo.svg';
import { ReactComponent as SCELogoMark } from '../assets/SCELogoMark.svg';
import { useAuth } from '../context/AuthContext';
import appConfig from '../config';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/products', label: 'Products', icon: Package },
  { path: '/apis', label: 'API Analytics', icon: Globe },
  { path: '/realtime', label: 'Real-Time', icon: Radio },
  { path: '/conversations', label: 'Conversations', icon: MessageSquare },
  { path: '/costs', label: 'Cost Center', icon: DollarSign },
  { path: '/users', label: 'User Activity', icon: Users },
  // Model Testing + Settings are restricted by role (requiredRole gating below).
  { path: '/model-testing', label: 'Model Testing', icon: FlaskConical, requiredRole: appConfig.rbac.modelTestRole },
  // Kill Switch is admin-only — same role gate as Settings.
  { path: '/traffic-controls', label: 'Traffic Controls', icon: ShieldAlert, requiredRole: appConfig.rbac.adminRole },
  { path: '/settings', label: 'Settings', icon: Settings, requiredRole: appConfig.rbac.adminRole },
];

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const { hasRole } = useAuth();
  // Hide role-gated items (e.g. Settings) from users who lack the role.
  const visibleNavItems = navItems.filter(
    (item) => !item.requiredRole || hasRole(item.requiredRole)
  );

  return (
    <>
      {/* Mobile backdrop — tap to close the drawer. Only rendered <lg. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={clsx(
          'flex flex-col bg-sce-green text-white transition-transform duration-200 no-print',
          // On mobile the sidebar is a fixed off-canvas drawer that slides in
          // from the left; on lg+ it becomes a normal in-flow column.
          'fixed inset-y-0 left-0 z-40 w-[260px]',
          'lg:static lg:z-auto lg:translate-x-0 lg:transition-all',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop width follows the collapse state.
          collapsed ? 'lg:w-16' : 'lg:w-[260px]'
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-14 px-4 bg-sce-green text-white border-b border-white/15">
          {/* Compact SCE flag mark — shown ONLY on collapsed desktop. Hidden on
              mobile (where the full logo is always used) to avoid two logos. */}
          <SCELogoMark
            className={clsx('h-7 w-auto shrink-0 hidden', collapsed && 'lg:block')}
            aria-label="SCE Edison"
          />
          {/* Full SCE Edison logo — always shown on mobile; on desktop hidden
              only when the sidebar is collapsed. */}
          <SCELogo
            className={clsx(
              'h-7 w-auto shrink-0',
              collapsed && 'lg:hidden'
            )}
            aria-label="SCE Edison"
          />
          {/* Close button — mobile only. */}
          <button
            onClick={onMobileClose}
            className="ml-auto lg:hidden p-1 text-white/80 hover:text-white"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
          {visibleNavItems.map(({ path, label, icon: Icon }) => {
            const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
            return (
              <NavLink
                key={path}
                to={path}
                onClick={onMobileClose}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                )}
                title={collapsed ? label : undefined}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className={clsx(collapsed && 'lg:hidden')}>{label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse Toggle — desktop only (mobile uses the X / backdrop). */}
        <button
          onClick={onToggle}
          className="hidden lg:flex items-center justify-center h-10 border-t border-white/15 text-white/70 hover:text-white transition"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>
    </>
  );
}
